import React, { useState, useCallback } from 'react';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { PersonPlus, InfoCircle, ArrowClockwise } from 'react-bootstrap-icons';
import studentService from '../../services/studentService';

const StudentForm = ({ onStudentAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) return 'Name can only contain letters, spaces, hyphens, and apostrophes';
        return '';
      
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Please enter a valid email address';
        return '';
      
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        if (!/^[\+]?[1-9][\d]{0,15}$/.test(value.trim().replace(/[\s\-\(\)]/g, ''))) {
          return 'Please enter a valid phone number';
        }
        return '';
      
      default:
        return '';
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) setError('');

    // Validate field and update validation errors
    const fieldError = validateField(name, value);
    setValidationErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));
  }, [error]);

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) errors[field] = error;
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fix the validation errors below');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await studentService.addStudent({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim()
      });

      // Reset form on success
      setFormData({ name: '', email: '', phone: '' });
      setValidationErrors({});
      
      if (onStudentAdded) {
        onStudentAdded(result);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add student. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formData, onStudentAdded]);

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '' });
    setValidationErrors({});
    setError('');
  };

  return (
    <Card className="shadow-sm rounded border-0">
      <Card.Header className="d-flex align-items-center bg-primary text-white">
        <PersonPlus size={24} className="me-2" />
        <h5 className="mb-0">Add New Student</h5>
      </Card.Header>
      <Card.Body className="p-4">
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} noValidate>
          <Row>
            <Col md={6}>
              <Form.Floating className="mb-3">
                <Form.Control
                  id="studentName"
                  name="name"
                  type="text"
                  placeholder="Enter student name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  autoComplete="name"
                  isInvalid={!!validationErrors.name}
                  className={validationErrors.name ? 'is-invalid' : formData.name ? 'is-valid' : ''}
                />
                <Form.Label htmlFor="studentName">Full Name *</Form.Label>
                {validationErrors.name && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.name}
                  </Form.Control.Feedback>
                )}
              </Form.Floating>
            </Col>

            <Col md={6}>
              <Form.Floating className="mb-3">
                <Form.Control
                  id="studentEmail"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  autoComplete="email"
                  isInvalid={!!validationErrors.email}
                  className={validationErrors.email ? 'is-invalid' : formData.email ? 'is-valid' : ''}
                />
                <Form.Label htmlFor="studentEmail">Email Address *</Form.Label>
                {validationErrors.email && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.email}
                  </Form.Control.Feedback>
                )}
              </Form.Floating>
            </Col>
          </Row>

          <Form.Floating className="mb-4">
            <Form.Control
              id="studentPhone"
              name="phone"
              type="tel"
              placeholder="Enter phone number"
              value={formData.phone}
              onChange={handleChange}
              required
              maxLength={20}
              autoComplete="tel"
              isInvalid={!!validationErrors.phone}
              className={validationErrors.phone ? 'is-invalid' : formData.phone ? 'is-valid' : ''}
            />
            <Form.Label htmlFor="studentPhone">Phone Number *</Form.Label>
            <Form.Text className="text-muted">
              📞 Include country code (e.g., +1 234 567 8900)
            </Form.Text>
            {validationErrors.phone && (
              <Form.Control.Feedback type="invalid">
                {validationErrors.phone}
              </Form.Control.Feedback>
            )}
          </Form.Floating>

          <div className="d-flex gap-2">
            <Button
              type="submit"
              variant="primary"
              disabled={loading || Object.keys(validationErrors).some(key => validationErrors[key])}
              className="flex-grow-1 d-flex align-items-center justify-content-center gap-2 fw-bold"
              size="md"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Adding Student...
                </>
              ) : (
                <>
                  <PersonPlus size={18} />
                  Add Student
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="outline-secondary"
              onClick={resetForm}
              disabled={loading}
              size="md"
              title="Reset Form"
            >
              <ArrowClockwise size={18} />
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default StudentForm;
